import {
	clearMiss,
	MATCH_START,
	matchTileState,
	MISS_MS,
	tapMatch,
	type MatchSide,
} from "@aybuben/client/lesson/match.ts";
import type { MatchStep } from "@aybuben/core/lesson.ts";
import { useState } from "react";

import { useStrings } from "../../i18n/index.tsx";
import { COL } from "../../theme.ts";
import { useSound } from "../../ui/sound.tsx";
import { Tile, type TileState } from "../kit.tsx";
import { registerView, type ExerciseViewProps } from "../view-registry.ts";

function MatchView({ step, onScore, onComplete }: ExerciseViewProps<MatchStep, string>) {
	const t = useStrings();
	const sound = useSound();
	const [board, setBoard] = useState(MATCH_START);
	const tap = (side: MatchSide, id: string) => {
		const r = tapMatch(board, side, id, step.left.length);
		if (!r) return;
		// A sound tile's tap is also its replay; glyph tiles stay silent, since
		// sounding a letter would give its pair away.
		const opt = (side === "L" ? step.left : step.right).find((o) => o.id === id);
		if (opt?.face.kind === "audio") sound.play(opt.face.asset);
		setBoard(r.board);
		if (r.score) onScore(r.score.key, r.score.ok, r.score.picked);
		if (r.complete) onComplete();
		if (r.board.miss) setTimeout(() => setBoard(clearMiss), MISS_MS);
	};
	const stateOf = (side: MatchSide, id: string): TileState => matchTileState(board, side, id);
	return (
		<div>
			<h2 className="text-xl font-extrabold">{t("lesson.matchTitle")}</h2>
			<div className="mt-6 grid grid-cols-2 gap-3">
				<div className="flex flex-col gap-3">
					{step.left.map((o) => (
						<Tile
							key={o.id}
							opt={o}
							height={70}
							state={stateOf("L", o.id)}
							disabled={board.matched.includes(o.id)}
							onClick={() => tap("L", o.id)}
						/>
					))}
				</div>
				<div className="flex flex-col gap-3">
					{step.right.map((o) => (
						<Tile
							key={o.id}
							opt={o}
							height={70}
							state={stateOf("R", o.id)}
							disabled={board.matched.includes(o.id)}
							onClick={() => tap("R", o.id)}
						/>
					))}
				</div>
			</div>
		</div>
	);
}

function MatchFooter() {
	const t = useStrings();
	return (
		<div className="py-3.5 text-center text-sm font-semibold" style={{ color: COL.mute }}>
			{t("lesson.matchHint")}
		</div>
	);
}

function MatchFeedback() {
	const t = useStrings();
	return <div className="mt-1 text-sm">{t("lesson.allMatched")}</div>;
}

// action "none": a match is never "checked" — it completes itself when the
// last pair lands, and until then the action slot carries a hint.
registerView<MatchStep, string>("match", {
	View: MatchView,
	Feedback: MatchFeedback,
	action: "none",
	Footer: MatchFooter,
});
